import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  Home, 
  FileText, 
  Newspaper, 
  Settings, 
  LogOut, 
  Menu,
  X,
  Users,
  AlertTriangle,
  Clock,
  CalendarDays,
  Wrench,
  LifeBuoy,
  Inbox,
  Send,
  ChevronDown,
  LineChart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from '../components/ui/alert-dialog';

const API = '/api';

export default function Layout({ children }) {
  const { user, logout, isAdmin, canViewOverviews, canManageIt, sessionExpiry, sessionExpired, handleSessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [announcement, setAnnouncement] = useState(null);
  const marqueeRef = useRef(null);
  const [marqueeDuration, setMarqueeDuration] = useState(12);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  // Session countdown timer
  useEffect(() => {
    if (!sessionExpiry || sessionExpired) return;
    const tick = () => {
      const remaining = Math.max(0, sessionExpiry - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) {
        handleSessionExpired();
        setShowExpiredModal(true);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionExpiry, sessionExpired, handleSessionExpired]);

  // Show modal when sessionExpired is set externally
  useEffect(() => {
    if (sessionExpired) setShowExpiredModal(true);
  }, [sessionExpired]);

  const formatTime = useCallback((ms) => {
    if (ms == null) return '--:--';
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, []);

  const handleExpiredRedirect = async () => {
    setShowExpiredModal(false);
    try { await logout(); } catch {}
    navigate('/login');
  };

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const { data } = await axios.get(`${API}/announcements/active`, { withCredentials: true });
        setAnnouncement(data);
      } catch {
        setAnnouncement(null);
      }
    };
    fetchAnnouncement();
    const interval = setInterval(fetchAnnouncement, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (marqueeRef.current && announcement?.text) {
      const textWidth = marqueeRef.current.scrollWidth;
      const speed = 80; // px per second
      setMarqueeDuration(Math.max(8, textWidth / speed));
    }
  }, [announcement]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/', label: 'Domů', icon: Home },
    { path: '/directory', label: 'Adresář', icon: Users },
    { path: '/documents', label: 'Dokumenty', icon: FileText },
    { path: '/blog', label: 'Blog', icon: Newspaper },
    ...(canViewOverviews ? [{ path: '/analytika', label: 'Analytika', icon: LineChart }] : []),
    { path: '/udrzba', label: 'Údržba', icon: Wrench },
    {
      label: 'IT',
      icon: LifeBuoy,
      pathPrefix: '/it',
      children: [
        { path: '/it/pozadavek-servis', label: 'Požadavek servis', icon: Send },
        ...(canManageIt ? [{ path: '/it/pozadavky', label: 'Požadavky', icon: Inbox }] : []),
      ],
    },
    { path: '/dovolena', label: 'Dovolená', icon: CalendarDays },
  ];

  if (isAdmin) {
    navItems.push({ path: '/admin', label: 'Administrace', icon: Settings });
  }

  const isActive = (path) => location.pathname === path || (path === '/analytika' && (location.pathname.startsWith('/analytika/') || location.pathname === '/prehledy'));

  return (
    <div className="min-h-screen bg-[#F8FAF8]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0] shadow-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_vitality-connect-3/artifacts/8w9yb7do_zdravestravovani_logo.png" 
                alt="Zdravé stravování" 
                className="h-10 object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                if (item.children) {
                  const isChildActive = item.children.some(c => isActive(c.path)) || (item.pathPrefix && location.pathname.startsWith(item.pathPrefix));
                  return (
                    <div key={item.label} className="relative group">
                      <button
                        data-testid={`nav-${item.label.toLowerCase()}`}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                          isChildActive ? 'bg-[#F0FDF4] text-[#16A34A]' : 'text-[#57534E] hover:text-[#16A34A] hover:bg-[#F0FDF4]'
                        }`}
                      >
                        <item.icon className="w-4 h-4" />
                        {item.label}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[200px]">
                        <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-lg p-1">
                          {item.children.map(child => (
                            <Link
                              key={child.path}
                              to={child.path}
                              data-testid={`nav-${child.path.replace(/\//g, '-')}`}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                isActive(child.path) ? 'bg-[#F0FDF4] text-[#16A34A]' : 'text-[#57534E] hover:bg-[#F0FDF4] hover:text-[#16A34A]'
                              }`}
                            >
                              <child.icon className="w-4 h-4" />
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      isActive(item.path)
                        ? 'bg-[#F0FDF4] text-[#16A34A]'
                        : 'text-[#57534E] hover:text-[#16A34A] hover:bg-[#F0FDF4]'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* User Menu */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-sm">
                <span className="text-[#57534E]">Přihlášen: </span>
                <span className="text-[#1C1917] font-medium">{user?.name}</span>
                {isAdmin && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-[#FEF3C7] text-[#92400E] rounded-full font-semibold">
                    Admin
                  </span>
                )}
              </div>
              <div
                data-testid="session-timer"
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold tabular-nums ${
                  timeLeft != null && timeLeft < 5 * 60 * 1000
                    ? 'bg-[#FEE2E2] text-[#DC2626]'
                    : 'bg-[#F0FDF4] text-[#16A34A]'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                {formatTime(timeLeft)}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="text-[#57534E] hover:text-[#EF4444] hover:bg-[#FEE2E2]"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Odhlásit
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-[#57534E] hover:text-[#16A34A]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#E2E8F0] bg-white">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                if (item.children) {
                  return (
                    <div key={item.label}>
                      <div className="flex items-center gap-3 px-4 py-2 text-xs uppercase font-semibold text-[#94A3B8]">
                        <item.icon className="w-4 h-4" /> {item.label}
                      </div>
                      {item.children.map(child => (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-3 px-8 py-3 rounded-xl text-sm font-medium transition-all ${
                            isActive(child.path) ? 'bg-[#F0FDF4] text-[#16A34A]' : 'text-[#57534E] hover:text-[#16A34A] hover:bg-[#F0FDF4]'
                          }`}
                        >
                          <child.icon className="w-5 h-5" />
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive(item.path)
                        ? 'bg-[#F0FDF4] text-[#16A34A]'
                        : 'text-[#57534E] hover:text-[#16A34A] hover:bg-[#F0FDF4]'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              <div className="flex items-center gap-2 px-4 py-2">
                <div
                  data-testid="session-timer-mobile"
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono font-semibold tabular-nums ${
                    timeLeft != null && timeLeft < 5 * 60 * 1000
                      ? 'bg-[#FEE2E2] text-[#DC2626]'
                      : 'bg-[#F0FDF4] text-[#16A34A]'
                  }`}
                >
                  <Clock className="w-3.5 h-3.5" />
                  {formatTime(timeLeft)}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-[#57534E] hover:text-[#EF4444] hover:bg-[#FEE2E2] w-full"
              >
                <LogOut className="w-5 h-5" />
                Odhlásit se
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Announcement Banner */}
      {announcement && announcement.text && (
        <div className="bg-[#FEF3C7] border-b border-[#FDE68A]" data-testid="announcement-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 py-2.5 overflow-hidden">
              <AlertTriangle className="w-4 h-4 text-[#92400E] flex-shrink-0" />
              <div className="overflow-hidden flex-1">
                <span
                  ref={marqueeRef}
                  className="marquee-single text-sm font-medium text-[#92400E]"
                  style={{ animationDuration: `${marqueeDuration}s` }}
                >
                  {announcement.text}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Session Expired Modal */}
      <AlertDialog open={showExpiredModal}>
        <AlertDialogContent data-testid="session-expired-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center text-lg">
              Relace vypršela
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center text-base">
              Čas přihlášení vypršel, přihlaste se znovu
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="justify-center sm:justify-center">
            <AlertDialogAction
              data-testid="session-expired-redirect-btn"
              onClick={handleExpiredRedirect}
              className="bg-[#16A34A] hover:bg-[#15803D] text-white"
            >
              Přejít na přihlášení
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
